import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, AlertTriangle, LayoutDashboard, Accessibility, Car, Users, ArrowUpDown, ShieldCheck, Database, Settings2, CalendarRange } from "lucide-react";
import FolderTabLayout from "./ui/folder-tab-layout";

import {
  useFleetPulseData,
  useFilteredFleetPulseData,
} from "./fleet-pulse/useFleetPulseData";
import { FleetPulseFilters as FPFilters } from "./fleet-pulse/types";
import { safeArr } from "./fleet-pulse/helpers";
import FleetPulseFiltersBar from "./fleet-pulse/FleetPulseFilters";
import OverviewTab from "./fleet-pulse/OverviewTab";
import JobsTrendsTab from "./fleet-pulse/JobsTrendsTab";
import TotalMobilityTab from "./fleet-pulse/TotalMobilityTab";
import VehiclesTab from "./fleet-pulse/VehiclesTab";
import DriversTab from "./fleet-pulse/DriversTab";
import HoistDemandTab from "./fleet-pulse/HoistDemandTab";
import TMClientCoverageTab from "./fleet-pulse/TMClientCoverageTab";
import DataGapsTab from "./fleet-pulse/DataGapsTab";
import TaxiCallerSetupCheckTab from "./fleet-pulse/TaxiCallerSetupCheckTab";


type FleetPulsePeriodFilters = FPFilters & {
  years: string[];
  months: string[];
};

const MONTHS = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

function defaultFilters(): FleetPulsePeriodFilters {
  const currentYear = String(new Date().getFullYear());

  return {
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear}-12-31`,

    // Keep these for backwards compatibility with existing filter logic/components
    year: "all",
    month: "all",

    // New analysis-period filters
    years: [currentYear],
    months: ["all"],

    vehicleId: "all",
    driverId: "all",
    region: "all",
    hoistOnly: false,
    fleetScope: "all",
    driverScope: "all",
  };
}

function getRowDate(row: any): string {
  return String(
    row?.trip_date ||
      row?.Trip_Date ||
      row?.pu_date ||
      row?.PU_Date ||
      row?.pickup_date ||
      row?.created_at ||
      row?.date ||
      ""
  ).slice(0, 10);
}

function rowMatchesPeriod(row: any, years: string[], months: string[]) {
  const date = getRowDate(row);
  if (!date || date.length < 7) return false;

  const rowYear = date.slice(0, 4);
  const rowMonth = date.slice(5, 7);

  const yearOk =
    years.includes("all") || years.length === 0 || years.includes(rowYear);

  const monthOk =
    months.includes("all") || months.length === 0 || months.includes(rowMonth);

  return yearOk && monthOk;
}

function getComparisonYears(years: string[]) {
  if (years.includes("all") || years.length === 0) return ["all"];

  return years
    .map((year) => String(Number(year) - 1))
    .filter((year) => year !== "NaN");
}

/**
 * Compute the minimal data range to load based on the currently selected years.
 * This makes the page load much faster: instead of always pulling 2020→now,
 * we only load the selected year(s) plus the previous year (for year-on-year
 * comparison). When "all" is selected we fall back to the broad range.
 */
function getDataRangeForYears(years: string[]) {
  const currentYear = new Date().getFullYear();

  if (years.includes("all") || years.length === 0) {
    return { startDate: "2020-01-01", endDate: `${currentYear}-12-31` };
  }

  const numeric = years
    .map((y) => Number(y))
    .filter((y) => !Number.isNaN(y));

  if (numeric.length === 0) {
    return { startDate: "2020-01-01", endDate: `${currentYear}-12-31` };
  }

  // Include previous year so year-on-year comparison still works.
  const minYear = Math.min(...numeric) - 1;
  const maxYear = Math.max(...numeric);

  return {
    startDate: `${minYear}-01-01`,
    endDate: `${maxYear}-12-31`,
  };
}

const FleetPulse: React.FC = () => {
  const [filters, setFilters] =
    useState<FleetPulsePeriodFilters>(defaultFilters);
  const [tab, setTab] = useState("overview");

  // Only load the range we actually need (selected years + previous year).
  // Loads the current year first, then expands as more years are selected.
  const broadRange = useMemo(
    () => getDataRangeForYears(filters.years),
    [filters.years]
  );

  // Load enough data so year/month selectors can work as an overview tool
  const base = useFleetPulseData(broadRange.startDate, broadRange.endDate);


  const hookFilters: FPFilters = useMemo(
    () => ({
      ...filters,
      startDate: broadRange.startDate,
      endDate: broadRange.endDate,

      // Let the hook do vehicle/driver/region/hoist filtering.
      // We do the multi-year/month filtering below.
      year: "all",
      month: "all",
    }),
    [filters, broadRange.startDate, broadRange.endDate]
  );

  const filteredByStandardFilters = useFilteredFleetPulseData(base, hookFilters);

  const filtered = useMemo(() => {
    return {
      ...filteredByStandardFilters,
      rw: safeArr(filteredByStandardFilters.rw).filter((row) =>
        rowMatchesPeriod(row, filters.years, filters.months)
      ),
      tc: safeArr(filteredByStandardFilters.tc).filter((row) =>
        rowMatchesPeriod(row, filters.years, filters.months)
      ),
    };
  }, [filteredByStandardFilters, filters.years, filters.months]);

  const comparisonFilters = useMemo(() => {
    return {
      ...filters,
      years: getComparisonYears(filters.years),
      months: filters.months,
    };
  }, [filters]);

  const prevFiltered = useMemo(() => {
    return {
      ...filteredByStandardFilters,
      rw: safeArr(filteredByStandardFilters.rw).filter((row) =>
        rowMatchesPeriod(
          row,
          comparisonFilters.years,
          comparisonFilters.months
        )
      ),
      tc: safeArr(filteredByStandardFilters.tc).filter((row) =>
        rowMatchesPeriod(
          row,
          comparisonFilters.years,
          comparisonFilters.months
        )
      ),
    };
  }, [filteredByStandardFilters, comparisonFilters]);

  const vehicleOptions = useMemo(() => {
    const set = new Set<string>();

    for (const r of safeArr(base.ridewise)) {
      if (r.vehicle_identifier) set.add(String(r.vehicle_identifier));
    }

    return Array.from(set).sort();
  }, [base.ridewise]);

  const driverOptions = useMemo(() => {
    const set = new Set<string>();

    for (const r of safeArr(base.ridewise)) {
      if (r.driver_number) set.add(String(r.driver_number));
    }

    const driversById = new Map(
      safeArr(base.drivers).map((d) => [String(d.ridewise_id || ""), d])
    );

    return Array.from(set)
      .sort()
      .map((id) => {
        const d = driversById.get(id);
        const name =
          d?.driver_name ||
          [d?.driver_firstname, d?.driver_lastname].filter(Boolean).join(" ");

        return {
          id,
          label: name ? `${name} (${id})` : id,
        };
      });
  }, [base.ridewise, base.drivers]);

  const regionOptions = useMemo(() => {
    const set = new Set<string>();

    for (const r of safeArr(base.ridewise)) {
      if (r.region) set.add(String(r.region));
    }

    return Array.from(set).sort();
  }, [base.ridewise]);

  const yearOptions = useMemo(() => {
    const set = new Set<string>();

    for (const r of safeArr(base.ridewise)) {
      const yr = getRowDate(r).slice(0, 4);
      if (yr) set.add(yr);
    }

    for (const r of safeArr(base.taxicaller)) {
      const yr = getRowDate(r).slice(0, 4);
      if (yr) set.add(yr);
    }

    return Array.from(set).sort();
  }, [base.ridewise, base.taxicaller]);

  const monthOptions = useMemo(() => MONTHS, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-yellow-500" />
          <h2 className="text-2xl font-bold golden-text">Fleet Pulse</h2>
        </div>

        <div className="text-xs text-muted-foreground">
          {base.loading
            ? "Loading…"
            : `RideWise: ${safeArr(
                base.ridewise
              ).length.toLocaleString()} rows · TaxiCaller: ${safeArr(
                base.taxicaller
              ).length.toLocaleString()} rows`}
        </div>
      </div>

      {base.error && (
        <Card className="bg-red-950/40 border border-red-800">
          <CardContent className="py-4 flex items-start gap-2 text-red-300">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-semibold">
                Failed to load Fleet Pulse data
              </div>
              <div className="text-xs">{base.error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <FleetPulseFiltersBar
        filters={filters as any}
        setFilters={setFilters as any}
        vehicleOptions={vehicleOptions}
        driverOptions={driverOptions}
        regionOptions={regionOptions}
        yearOptions={yearOptions}
        monthOptions={monthOptions as any}
        onReset={() => setFilters(defaultFilters())}
      />

      <FolderTabLayout
        tabs={[
          { value: "overview", label: "Overview", icon: LayoutDashboard },
          { value: "jobstrends", label: "Jobs & Trends", icon: CalendarRange },
          { value: "tm", label: "Total Mobility", icon: Accessibility },
          { value: "vehicles", label: "Vehicles", icon: Car },
          { value: "drivers", label: "Drivers", icon: Users },
          { value: "hoist", label: "Hoist Demand", icon: ArrowUpDown },
          { value: "tmcoverage", label: "TM Client Coverage", icon: ShieldCheck },
          { value: "gaps", label: "Data Gaps", icon: Database },
          { value: "tcsetup", label: "TaxiCaller Setup", icon: Settings2 },
        ]}
        value={tab}
        onValueChange={setTab}
      >
        {tab === "overview" && (
          <OverviewTab
            rw={filtered.rw}
            prevRw={prevFiltered.rw}
            allRw={safeArr(filteredByStandardFilters.rw)}
            selectedYears={filters.years}
            selectedMonths={filters.months}
            loading={base.loading}
          />
        )}

        {tab === "jobstrends" && (
          <JobsTrendsTab
            tc={filtered.tc}
            rw={filtered.rw}
            allTc={safeArr(filteredByStandardFilters.tc)}
            allRw={safeArr(filteredByStandardFilters.rw)}
            selectedYears={filters.years}
            selectedMonths={filters.months}
            loading={base.loading}
          />
        )}

        {tab === "tm" && (
          <TotalMobilityTab rw={filtered.rw} loading={base.loading} />
        )}

        {tab === "vehicles" && (
          <VehiclesTab
            rw={filtered.rw}
            vehiclesById={filtered.vehiclesById}
            loading={base.loading}
          />
        )}

        {tab === "drivers" && (
          <DriversTab
            rw={filtered.rw}
            driversById={filtered.driversById}
            loading={base.loading}
          />
        )}

        {tab === "hoist" && (
          <HoistDemandTab
            rw={filtered.rw}
            allRw={safeArr(filteredByStandardFilters.rw)}
            selectedYears={filters.years}
            selectedMonths={filters.months}
            vehiclesById={filtered.vehiclesById}
            driversById={filtered.driversById}
            loading={base.loading}
          />
        )}

        {tab === "tmcoverage" && (
          <TMClientCoverageTab
            rw={filtered.rw}
            tc={filtered.tc}
            accounts={base.accounts}
            approvals={base.approvals}
            permanentBookings={base.permanentBookings}
            loading={base.loading}
          />
        )}

        {tab === "gaps" && (
          <DataGapsTab
            rw={filtered.rw}
            tc={filtered.tc}
            vehiclesById={filtered.vehiclesById}
            driversById={filtered.driversById}
            loading={base.loading}
          />
        )}

        {tab === "tcsetup" && (
          <TaxiCallerSetupCheckTab
            rw={filtered.rw}
            tc={filtered.tc}
            accounts={base.accounts}
            approvals={base.approvals}
            loading={base.loading}
          />
        )}
      </FolderTabLayout>

    </div>
  );
};

export default FleetPulse;